import {
	AddressTag,
	DataPersonTct,
	DocumentWorkflowInstanceSignatureStatus,
	SignableContract,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import { getCountryCodeByName } from '@dealstack/shared/lib/countries/countriesData';
import { isConcludedSignatureStatus } from '@dealstack/shared/lib/common/business/signatureStatus';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import type { QueryKey } from '@tanstack/react-query';

/**
 * How long documents may sit in "preparing" before the investor is offered a
 * retry. Inherited from the old generation poll's 25s cap — the poll is gone,
 * but with no fallback the escape hatch it provided is now the only thing
 * standing between a dropped generation signal and a stuck investor.
 */
export const MAX_GENERATION_WAIT_MS = 25000;

/** Canonical query key, shared by the query and the SSE hook's invalidation. */
export const buildSignableDocumentsQueryKey = ({
	excludeTsaContracts,
	investmentWorkflowId,
	investorId,
}: {
	excludeTsaContracts: boolean;
	investmentWorkflowId?: string | null;
	investorId: string;
}): QueryKey => [
	API_PATHS.SHAREHOLDER_CONTRACT_TRANSACTION_INVESTOR_INVESTORID_SIGNABLE_DOCUMENTS_GET,
	{
		excludeTsaContracts,
		investmentWorkflowId,
		investorId,
	},
];

/**
 * Check if the investor's own required signatures — PARTY and, when present,
 * their own PIV leg — are complete across all non-REVIEW contracts. PIV is always
 * self-signed by the investor (a distinct PIV signatory is only ever represented
 * via the REPRESENTATIVE role), so it counts as the investor's own work, unlike
 * witness/representative signatures from other parties, which this deliberately
 * excludes.
 */
export const isAllContractsSigned = (contracts: SignableContract[] | undefined): boolean => {
	if (!contracts || contracts.length === 0) {
		return true;
	}

	return contracts
		.filter((contract) => contract.signatureType !== SignatureType.REVIEW)
		.every(
			(contract) =>
				isSigningComplete(contract.status) &&
				(!contract.piv || isSigningComplete(contract.piv.status))
		);
};

/**
 * Check if the contract is ready to be signed. REVIEW documents carry no signature,
 * so they are never signable however the slot is parked.
 */
export const isContractSignable = (contract: SignableContract): boolean =>
	contract.status === DocumentWorkflowInstanceSignatureStatus.PENDING_SIGNATURE &&
	contract.signatureType !== SignatureType.REVIEW;

/**
 * Check if a signable form contract is ready for form submission
 */
export const isFormSubmittable = (contract: SignableContract): boolean => {
	return (
		contract.isSignableForm &&
		!contract.formSubmitted &&
		contract.status === DocumentWorkflowInstanceSignatureStatus.PENDING_SIGNATURE
	);
};

/**
 * Whether the investor may withdraw the form they uploaded. The server decides: the rule reads the
 * instance status and every other signer's state, neither of which this DTO carries.
 */
export const isFormRemovable = (contract: SignableContract): boolean => {
	return contract.canRemoveSubmittedForm;
};

/**
 * Nothing left for this signer to do. Delegates to the shared predicate the admin app gates on
 * too — the investor portal keeping its own copy of this list is what let DC-1098's
 * CLOSED_WITHOUT_SIGNATURE be handled on one side of the app and not the other.
 */
export const isSigningComplete = (
	status: DocumentWorkflowInstanceSignatureStatus | null | undefined
): boolean => {
	return isConcludedSignatureStatus(status);
};

/**
 * A signer's (investor or PIV person's) ISO 3166-1 alpha-2 country code for signature-method
 * selection: their tagged primary address, falling back to the first address on file.
 * `Address.country` is stored as the full country name (the KYC address form's
 * `COUNTRIES_OPTIONS` value), so it's resolved to an alpha-2 code here to match
 * `COUNTRY_SIGNING_METHOD_GROUPS`.
 */
export const getPrimaryAddressCountry = (
	person: Pick<DataPersonTct, 'addresses'> | null | undefined
): string | null => {
	const addresses = person?.addresses ?? [];
	const primaryAddress =
		addresses.find((address) => address.tags?.includes(AddressTag.PRIMARY_ADDRESS)) ??
		addresses[0];
	return getCountryCodeByName(primaryAddress?.country);
};

const isSignatureFullyDone = (
	status: DocumentWorkflowInstanceSignatureStatus | null | undefined
): boolean =>
	status === DocumentWorkflowInstanceSignatureStatus.SIGNED ||
	status === DocumentWorkflowInstanceSignatureStatus.COMPLETED;

/**
 * Check if ALL signatures across all parties are fully complete.
 * Unlike isAllContractsSigned(), this does NOT treat WAITING_FOR_WITNESS_SIGNATURES
 * as complete — it requires witness, PIV, and representative signatures to be SIGNED/COMPLETED too.
 */
export const areAllSignaturesFullyComplete = (
	contracts: SignableContract[] | undefined
): boolean => {
	if (!contracts || contracts.length === 0) {
		return true;
	}

	return contracts
		.filter((contract) => contract.signatureType !== SignatureType.REVIEW)
		.every((contract) => {
			const investorDone = isSignatureFullyDone(contract.status);

			// Wet-ink signing and witnessing happen together on paper, so once the
			// investor has uploaded the signed document the witness is considered done.
			const isWetInk = contract.signatureType === SignatureType.WET_INK;

			const witnessDone =
				isWetInk ||
				!contract.witness?.hasWitnessRole ||
				isSignatureFullyDone(contract.witness.status);

			const pivDone = !contract.piv || isSignatureFullyDone(contract.piv.status);

			const pivWitnessDone =
				isWetInk ||
				!contract.piv?.witness?.hasWitnessRole ||
				isSignatureFullyDone(contract.piv.witness.status);

			const repDone =
				!contract.representative || isSignatureFullyDone(contract.representative.status);

			const repWitnessDone =
				isWetInk ||
				!contract.representative?.witness?.hasWitnessRole ||
				isSignatureFullyDone(contract.representative.witness.status);

			return (
				investorDone &&
				witnessDone &&
				pivDone &&
				pivWitnessDone &&
				repDone &&
				repWitnessDone
			);
		});
};
